import React, { useState } from 'react';
import { PrimaryButton, ErrorButton } from "../UI";
import TableShimmer from '../UI/Shimmer/TableShimmer';
import { deleteEntityData, getEntityDataByQuery } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import { Link } from 'react-router-dom';
import AddEntityModal from './Modal/AddEntityModal';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import ConfirmModal from '../UI/Modal/confirmModal';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const ListEntities = () => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const [entityId, setEntityId] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState();
    const [success, setSuccess] = useState();
    const [showAddEntityModal, setShowAddEntityModal] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

    const getEntityData = async () => {
        try {
            setLoading(true);
            const result = await getEntityDataByQuery(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData?.defaultAdobeIO?.tenant_name,
                entityId
            );
            setError("")
            setData(result.data);
        } catch (error) {
            console.log("error getAllIntegrationData: ", error?.response?.data?.message);
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    const handleSearch = async () => {
        await getEntityData();
    }

    const handleCloseModal = () => {
        setShowAddEntityModal(false)
    }

    const handleDelete =async () => {
        try {
            setLoading(true);
            const result = await deleteEntityData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData?.defaultAdobeIO?.tenant_name,
                { id: data?.id, environment: data?.environment } 
            );
            if(result?.status) {
                setError("")
                setSuccess(true)
                setData(null)
                setEntityId("")
            }
        } catch (error) {
            console.log("error handleSubmit: ", error);
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            {success && <SuccessAlert disappearTime={content.LIST_ENTITIES_CONTENT.add_entity_modal.success_timeout} >{content.LIST_ENTITIES_CONTENT.delete_success}</SuccessAlert> }
            {showDeleteConfirmation && <ConfirmModal handleConfirmation={handleDelete} hideModal={() => setShowDeleteConfirmation(false)} title={content.LIST_ENTITIES_CONTENT.delete_confirmation}/>}
            <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">{content.LIST_ENTITIES_CONTENT.title}</h1>
            <div className="flex flex-col pb-6 w-full">
                <p>{content.LIST_ENTITIES_CONTENT.description}</p>
                <div className="flex justify-between w-full items-center mt-6">
                    <div className='flex gap-3'>
                        <input
                            value={entityId}
                            onChange={e => setEntityId(e.target.value)}
                            className="min-w-56 p-2.5 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none"
                            placeholder="Search By Entity ID"
                        />
                        <PrimaryButton handleClick={handleSearch}>
                            {content.LIST_ENTITIES_CONTENT.search_btn}
                        </PrimaryButton>
                    </div>
                    <div className='relative'>
                        <PrimaryButton handleClick={() => setShowAddEntityModal(true)}>
                            {content.LIST_ENTITIES_CONTENT.add_entity_btn}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
            <AddEntityModal showAddEntityModal={showAddEntityModal} handleCloseModal={handleCloseModal} />
            <div className='overflow-x-auto overflow-y-auto w-full'>
                {loading && <TableShimmer colSize={2} rowSize={6}/>}
                {!loading && error && <h3 className="text-center">{error}</h3>}
                {!loading && !error && !data && <h3 className="text-center">{content.LIST_ENTITIES_CONTENT.search_msg}</h3>}
                {!loading && !error && data && (
                    <div className="flex flex-col min-h-screen">
                        <header className="flex items-center gap-4 p-4 border-b">
                            <img src={data?.attributes.thumbnailUrl} alt={data?.name} className="w-32 h-32 object-cover" />
                            <div>
                                <h1 className="font-bold text-xl">{data?.name}</h1>
                                <Link
                                    className="inline-flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm"
                                    target='_blank'
                                    to={data?.attributes.pageUrl}
                                >
                                    {content.LIST_ENTITIES_CONTENT.entity_items.visit_site_btn}
                                </Link>
                            </div>
                        </header>
                        <div className="flex-col p-5">
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.id}:</div>
                                <div className="text-sm text-gray-500">${data?.id}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.environment}:</div>
                                <div className="text-sm text-gray-500">${data?.environment}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.categories}:</div>
                                <div className="text-sm text-gray-500">${data?.categories?.join(", ")}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.inventory}:</div>
                                <div className="text-sm text-gray-500">{data?.attributes.inventory}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.value}:</div>
                                <div className="text-sm text-gray-500">${data?.attributes.value}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.margin}:</div>
                                <div className="text-sm text-gray-500">${data?.attributes.margin}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.message}:</div>
                                <div className="text-sm text-gray-500">${data?.attributes.message}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.created_at}:</div>
                                <div className="text-sm text-gray-500">{data?.attributes.createdAt}</div>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                                <div className="font-medium w-40">{content.LIST_ENTITIES_CONTENT.entity_items.last_updated_at}:</div>
                                <div className="text-sm text-gray-500">{data?.attributes.lastUpdatedAt}</div>
                            </div>
                        </div>
                        <footer className="flex items-center gap-4 p-4 border rounded-md border-red-600 justify-between">
                            <div>
                                <p>{content.LIST_ENTITIES_CONTENT.delete_message_1}</p>
                                <p>{content.LIST_ENTITIES_CONTENT.delete_message_2}</p>
                            </div>
                            <ErrorButton handleClick={() => setShowDeleteConfirmation(true)}>{content.LIST_ENTITIES_CONTENT.delete_entity_btn}</ErrorButton>
                        </footer>
                    </div>
                )}
            </div>
        </>
    )
}

export default ListEntities;